import { Alert, Badge, Button, Code, CopyButton, Group, Image, Modal, Paper, PasswordInput, PinInput, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconShieldCheck } from '@tabler/icons-react';
import QRCode from 'qrcode';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResendVerification, useTotpDisable, useTotpEnable, useTotpSetup } from '@/api/account';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';

/** Zwei-Faktor-Authentifizierung (SEC-03) und E-Mail-Bestätigung (SEC-05). */
export function SecuritySection() {
  const { t } = useTranslation();
  const { user } = useCurrentWorkspace();
  const onError = useApiError();
  const setup = useTotpSetup();
  const enable = useTotpEnable();
  const disable = useTotpDisable();
  const resend = useResendVerification();
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [password, setPassword] = useState('');

  const start = () =>
    setup.mutate(undefined, {
      onSuccess: async (data) => setQr(await QRCode.toDataURL(data.otpauthUrl, { margin: 1, width: 200 })),
      onError,
    });

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Title order={5}>{t('security.title')}</Title>
        {user.twoFactorEnabled && (
          <Badge color="green" leftSection={<IconShieldCheck size={12} />}>
            {t('security.enabled')}
          </Badge>
        )}
      </Group>
      <Stack>
        {!user.emailVerified && (
          <Alert color="yellow">
            <Group justify="space-between">
              <Text size="sm">{t('security.emailNotVerified')}</Text>
              <Button
                size="xs"
                variant="light"
                loading={resend.isPending}
                onClick={() => resend.mutate(undefined, { onSuccess: () => notifications.show({ message: t('security.verificationSent') }), onError })}
              >
                {t('security.resend')}
              </Button>
            </Group>
          </Alert>
        )}
        <Text size="sm" c="dimmed">
          {t('security.twoFactorText')}
        </Text>
        {user.twoFactorEnabled ? (
          <Group>
            <Button variant="light" color="red" onClick={() => setDisableOpen(true)}>
              {t('security.disable')}
            </Button>
          </Group>
        ) : setup.data && qr ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Stack align="center">
              <Image src={qr} w={200} h={200} alt={t('security.qrAlt')} />
              <Code>{setup.data.secret}</Code>
            </Stack>
            <Stack>
              <Text size="sm">{t('security.scanHint')}</Text>
              <PinInput length={6} type="number" oneTimeCode value={code} onChange={setCode} aria-label={t('login.code')} />
              <Group>
                <Button
                  disabled={code.length !== 6}
                  loading={enable.isPending}
                  onClick={() =>
                    enable.mutate(code, {
                      onSuccess: (r) => {
                        setRecovery(r.codes);
                        setQr(null);
                        setCode('');
                      },
                      onError,
                    })
                  }
                >
                  {t('security.activate')}
                </Button>
                <Button variant="default" onClick={() => setQr(null)}>
                  {t('common.cancel')}
                </Button>
              </Group>
            </Stack>
          </SimpleGrid>
        ) : (
          <Group>
            <Button onClick={start} loading={setup.isPending}>
              {t('security.setup')}
            </Button>
          </Group>
        )}
      </Stack>

      <Modal opened={!!recovery} onClose={() => setRecovery(null)} title={t('security.recoveryTitle')} closeOnClickOutside={false}>
        <Stack>
          <Text size="sm">{t('security.recoveryText')}</Text>
          <SimpleGrid cols={2}>
            {recovery?.map((c) => (
              <Code key={c} ta="center">
                {c}
              </Code>
            ))}
          </SimpleGrid>
          <Group justify="flex-end">
            <CopyButton value={recovery?.join('\n') ?? ''}>
              {({ copied, copy }) => (
                <Button variant="default" onClick={copy}>
                  {copied ? t('common.copied') : t('security.copyCodes')}
                </Button>
              )}
            </CopyButton>
            <Button onClick={() => setRecovery(null)}>{t('common.done')}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={disableOpen} onClose={() => setDisableOpen(false)} title={t('security.disable')}>
        <Stack>
          <PasswordInput label={t('profile.currentPassword')} value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDisableOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              loading={disable.isPending}
              onClick={() =>
                disable.mutate(password, {
                  onSuccess: () => {
                    setDisableOpen(false);
                    setPassword('');
                  },
                  onError,
                })
              }
            >
              {t('security.disable')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
